/**
 * The markup both search-field twins draw. One is a Server Component and the
 * other a "use client" leaf, so neither can import the other; only what both
 * module kinds can render lives here, and the interaction stays in each twin.
 */
import type { ReactNode } from "react";
import styles from "./search-field.module.css";
import { warnAboutAppearanceOnce } from "../appearance-warnings";
import { CLOSE_GLYPH_PATH, CLOSE_GLYPH_VIEWBOX } from "../close-glyph";
import { INERT_SLOT } from "../inert-slot";
import {
  SURFACE_PRESETS,
  isSurfacePreset,
  resolveSurface,
  resolveSurfaceAttribute,
  type SurfacePreset
} from "../../tokens/surface-presets";
import { withSafeRender } from "../safe-render";

/**
 * The published magnifying glass, inlined — DDS imports no icon package. Its
 * own export fill is dropped for `currentColor` at the call sites.
 */
export const SEARCH_GLYPH_VIEWBOX = "0 0 32 32";

export const SEARCH_GLYPH_PATH =
  "M4.44444 13.7939C4.44444 16.0506 5.3185 18.1835 6.9165 19.7764C8.5145 " +
  "21.3783 10.6334 22.2544 12.8847 22.2544C14.571 22.2544 16.1866 21.7588 " +
  "17.5816 20.8207L23.8147 27.0688C24.1325 27.3874 24.5474 27.5556 24.9977 " +
  "27.5556C25.448 27.5556 25.8629 27.3785 26.1808 27.0688C26.4986 26.7502 " +
  "26.6752 26.3342 26.6664 25.8829C26.6664 25.4316 26.4898 25.0156 26.1808 " +
  "24.697L19.9389 18.4401C20.8571 17.0595 21.3338 15.4577 21.3338 " +
  "13.7939C21.3338 11.5371 20.4509 9.40432 18.8618 7.81133C17.2638 6.20949 " +
  "15.1449 5.33333 12.8935 5.33333C10.6422 5.33333 8.51448 6.20949 6.92531 " +
  "7.81133C5.32731 9.41317 4.45328 11.5371 4.45328 13.7939H4.44444ZM18.0142 " +
  "13.785C18.0142 16.6082 15.7187 18.9091 12.9024 18.9091C10.086 18.9091 " +
  "7.79054 16.6082 7.79054 13.785C7.79054 10.9619 10.086 8.66092 12.9024 " +
  "8.66092C15.7187 8.66092 18.0142 10.9619 18.0142 13.785Z";

/* ─────────────────────────────────────────────────────────────────────────────
   THE TWO APPEARANCE VOCABULARIES: GLASS AND RIM.

   Both are published by ../../tokens/surface.css and both are driven by a
   `data-` attribute on the pill, never an inline style. surface.css spells out
   why an inline style is forbidden (a CSS-value passthrough lets a caller put
   `url()` inside a filter) and why an unknown name renders the Figma default
   rather than throwing.

   ⚠ NEITHER PROP DEFAULTS TO A PRESET NAME. Omitted means omitted: the attribute
   is left off entirely, no selector matches, and the pill renders what it
   rendered before either prop existed. A spec asserts that the default markup
   carries no `data-dds-surface` and no `data-dds-rim`.
   ───────────────────────────────────────────────────────────────────────────*/

/* The glass vocabulary is NOT declared here. It lives once in
   ../../tokens/surface-presets, beside the stylesheet that publishes the steps,
   and is re-exported so the stories and the specs keep resolving it from here. */
export { SURFACE_PRESETS, isSurfacePreset, resolveSurface, resolveSurfaceAttribute };
export type { SurfacePreset };

/**
 * The rim intensities. **Exactly one of these is measured design**, and it is
 * the one with no name: omitting `rim` renders the four `--surface-rim-*`
 * defaults, sampled off a **1x** export of set 12331:8398 — 11% top edge, 8%
 * bottom edge, and two `plus-lighter` arc DELTAS of 9% and 6% that compose to
 * the frame's 20% and 14% peaks.
 *
 * ⚠ SAMPLE AT 1x, or average the 4x band — never take the 4x peak. Painted
 * across a full 1 CSS px band it integrates to roughly 5x the edge the frame
 * renders, which makes the rim's DESIGNED corner gaps read as "the border stops
 * at both ends".
 *
 * Every named step below is an engineering scale around that measurement,
 * offered because QA asked for shade configurability. None is a published Figma
 * value and none should ever be cited as one:
 *
 * - `soft`   — roughly half the measured intensity, for a rim over a light or
 *              already-bright backdrop where the full value reads as a seam.
 * - `bright` — brighter than measured. ⚠ NOT the answer to the "in-panel field
 *              looks dim" report: Figma draws the SAME rim on the standalone and
 *              the in-panel field, and the readings that establish it are in
 *              design-conflicts/search-field.mdx.
 * - `flat`   — top and bottom equal, arc layer dropped.
 * - `none`   — no rim at all, for a host that draws its own edge.
 */
export const RIM_PRESETS = ["soft", "bright", "flat", "none"] as const;
export type RimPreset = (typeof RIM_PRESETS)[number];

/**
 * The pill's class list. Both twins take the same three inputs and compose them
 * the same way, so there is one builder.
 *
 * ⚠ THE TWO FLAGS ARE `leading` / `trailing`, NOT `icon` / `clearable`. What the
 * layout cares about is whether SOMETHING occupies that end of the pill, because
 * that is what the overlaid prompt has to clear: `icon` and `startIcon` both
 * occupy the lead end, `clearable` and `endIcon` both the trailing one. Pass the
 * OR, not the prop. (The class it emits for the trailing case is still
 * `.clearable`, named for the control that first needed the space.)
 */
export function searchFieldClasses({
  leading,
  trailing,
  className
}: {
  leading?: boolean;
  trailing?: boolean;
  className?: string;
}) {
  return [styles.root, leading && styles.withIcon, trailing && styles.clearable, className]
    .filter(Boolean)
    .join(" ");
}

/**
 * The pill's appearance attributes — `data-dds-surface` and `data-dds-rim` —
 * plus the development-only diagnostics for the ways the appearance API can be
 * asked for something it will not do.
 *
 * One helper rather than two, so both twins spread ONE object onto the form and
 * cannot drift by wiring one attribute and forgetting the other. The keys are
 * ABSENT rather than undefined-valued when the props are omitted, because
 * absence is what the "omitted means omitted" spec asserts.
 */
export function searchFieldChrome({
  id,
  surface,
  rim,
  icon,
  startIcon,
  endIcon,
  clearable
}: {
  /* Only the diagnostics read it — it is what keys a collision warning to the
     call site that made it rather than to the process. */
  id: string;
  surface?: string;
  rim?: string;
  icon?: boolean;
  startIcon?: ReactNode;
  endIcon?: ReactNode;
  clearable?: boolean;
}): { "data-dds-surface"?: SurfacePreset; "data-dds-rim"?: string } {
  /* ⚠ ONE NORMALIZED VALUE, FED TO BOTH THE WARNING AND THE JSX. `surface` is an
     open string union because it is CMS-fed, and an unset CMS select round-trips
     as `""` or a lone space — not `undefined`, so a raw spread renders a PRESENT
     `data-dds-surface=""`. That is the one state surface.css's nested reset
     exists to prevent: it keys off ABSENCE, so a blank string is excluded from
     the reset AND matches no preset selector, leaving the pill inheriting an
     ancestor's fill. `rim` is normalized for the same CMS reason. */
  const resolvedSurface = resolveSurface(surface);
  const resolvedRim = resolveSurface(rim);

  if (process.env.NODE_ENV !== "production") {
    warnAboutAppearance({
      id,
      surface: resolvedSurface,
      rim: resolvedRim,
      icon,
      startIcon,
      endIcon,
      clearable
    });
  }

  /* The RENDERED surface is gated on the published presets, not just normalized:
     an unknown NON-blank name as a present attribute dodges surface.css's
     absence-keyed reset the same way a blank one did, and inherits a
     preset-carrying ancestor's tokens. `rim` is not gated — an unknown rim
     matches no selector and the bare-scope defaults hold, so it is inert. */
  const surfaceAttribute = resolveSurfaceAttribute(surface);

  return {
    ...(surfaceAttribute === undefined ? {} : { "data-dds-surface": surfaceAttribute }),
    ...(resolvedRim === undefined ? {} : { "data-dds-rim": resolvedRim })
  };
}

/**
 * Development-only diagnostics, compiled out of production builds. Every case
 * here renders something that merely LOOKS right — the published default glass,
 * the measured rim, one glyph where two were passed — with no browser signal at
 * all. The two preset checks exist because both props are typed
 * `Preset | (string & {})`: the union is autocomplete, not a gate.
 */
function warnAboutAppearance({
  id,
  surface,
  rim,
  icon,
  startIcon,
  endIcon,
  clearable
}: {
  id: string;
  surface?: string;
  rim?: string;
  icon?: boolean;
  startIcon?: ReactNode;
  endIcon?: ReactNode;
  clearable?: boolean;
}) {
  if (surface !== undefined && !isSurfacePreset(surface)) {
    warnAboutAppearanceOnce(
      `surface:${surface}`,
      `[SearchField] unknown surface "${surface}", so the field keeps its ` +
        "published glass (a 32% slate-950 fill behind a 4px blur). The steps " +
        `surface.css defines are ${SURFACE_PRESETS.join(", ")}.`
    );
  }

  if (rim !== undefined && !(RIM_PRESETS as readonly string[]).includes(rim)) {
    warnAboutAppearanceOnce(
      `rim:${rim}`,
      `[SearchField] unknown rim "${rim}", so the field keeps the measured ` +
        `Figma rim. The steps are ${RIM_PRESETS.join(", ")} — or omit the prop, ` +
        "which is the measured one."
    );
  }

  /* BOTH ENDS CAN ONLY HOLD ONE THING, and in both collisions the NEWER prop is
     the one that loses, so no existing call site can have its rendering changed
     by a prop it does not pass. Said out loud because the symptom is the passed
     node simply not appearing. Deduped and keyed on the field's `id`. */
  if (startIcon && icon) {
    warnAboutAppearanceOnce(
      `collision:icon+startIcon:${id}`,
      "[SearchField] both `icon` and `startIcon` were given. The lead end " +
        "holds one mark: `startIcon` renders and the built-in magnifying " +
        "glass is dropped. Drop `icon` to say that on purpose."
    );
  }

  if (endIcon && clearable) {
    warnAboutAppearanceOnce(
      `collision:clearable+endIcon:${id}`,
      "[SearchField] both `clearable` and `endIcon` were given. The trailing " +
        "end holds one thing and the clear control wins, because it is the " +
        "interactive one — `endIcon` is not rendered at all. Decorate the " +
        "lead end instead, or drop `clearable`."
    );
  }
}

/* Both slots are `inert`, so a caller's control is out of the tab order but
   painted and dead with nothing saying why; this says it. Client twin only —
   the server twin has no effect to run it from. */
export function warnAboutFocusableSlotContent(root: HTMLElement | null, id: string) {
  const focusable = root?.querySelector(
    "[inert] a[href], [inert] button, [inert] input, [inert] select, " +
      "[inert] textarea, [inert] [tabindex]"
  );

  if (!focusable) {
    return;
  }

  warnAboutAppearanceOnce(
    `slot-focusable:${id}`,
    `[SearchField] "${id}" was given a focusable <${focusable.tagName.toLowerCase()}> ` +
      "in `startIcon` or `endIcon`. Both slots are aria-hidden and inert, so " +
      "it is painted but cannot be reached or activated. Slots are decoration; " +
      "a control that belongs in the pill belongs in the component, named."
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   THE TWO SLOT BOXES.

   ⚠ BOTH ARE aria-hidden, AND THAT IS THE WHOLE SAFETY ARGUMENT. A slot takes an
   arbitrary ReactNode, and nothing can inspect one at render to tell whether it
   announces anything, so the slots are decorative BY CONSTRUCTION: whatever
   arrives is removed from the accessibility tree, the field's name still comes
   from its own label and its `role="search"`, and no slot can make the field
   nameless — WCAG 4.1.2, which this component has an AC against.

   The contract that buys it: DO NOT PUT AN INTERACTIVE ELEMENT IN A SLOT. A
   focusable node inside `aria-hidden` is a tab stop a screen reader cannot see,
   so both boxes carry `inert` as well. That stops the node being reached; it
   does not stop it being wrong.

   `inert` is spread from `INERT_SLOT` (`atoms/inert-slot.ts`) rather than
   written inline because the form the attribute has to take depends on which
   react-dom serialises it, so it cannot be a literal here — that module picks
   it off React's major version, and each form, on the runtime it is chosen
   for, lands as the same `inert=""` HTML. Its header and `inertSlotFor`
   between them carry the measurements, the two serialisers they were taken
   against, and the one type assertion the seam needs.

   GEOMETRY: each box is pinned to the measured size — `--sf-icon-size` (24px)
   and `--sf-clear-size` (36px) — so the overlaid prompt clears the same distance
   whether the mark is ours or the caller's. An `<svg>` child is scaled to fill
   it; anything else keeps its own size and is allowed to overflow VISIBLY rather
   than be cropped.
   ───────────────────────────────────────────────────────────────────────────*/

/** The lead end, when a caller substitutes its own mark for the glass. */
function LeadingSlotImpl({ children }: { children: ReactNode }) {
  return (
    <span aria-hidden="true" className={styles.iconSlot} {...INERT_SLOT}>
      {children}
    </span>
  );
}
export const LeadingSlot = withSafeRender(LeadingSlotImpl, "LeadingSlot");

/** The trailing end, when there is no clear control to occupy it. */
function TrailingSlotImpl({ children }: { children: ReactNode }) {
  return (
    <span aria-hidden="true" className={styles.endSlot} {...INERT_SLOT}>
      {children}
    </span>
  );
}
export const TrailingSlot = withSafeRender(TrailingSlotImpl, "TrailingSlot");

/**
 * The magnifying glass, decorative. `focusable="false"` as well as
 * `aria-hidden` — an SVG is focusable by default in some engines.
 */
function SearchGlyphImpl() {
  return (
    <svg
      aria-hidden="true"
      className={styles.icon}
      focusable="false"
      viewBox={SEARCH_GLYPH_VIEWBOX}
    >
      <path d={SEARCH_GLYPH_PATH} fill="currentColor" />
    </svg>
  );
}
export const SearchGlyph = withSafeRender(SearchGlyphImpl, "SearchGlyph");

/**
 * The X inside the clear control. Only the glyph is shared; the button around
 * it differs between the twins.
 */
function ClearGlyphImpl() {
  return (
    <svg
      aria-hidden="true"
      className={styles.clearGlyph}
      focusable="false"
      viewBox={CLOSE_GLYPH_VIEWBOX}
    >
      <path d={CLOSE_GLYPH_PATH} fill="currentColor" />
    </svg>
  );
}
export const ClearGlyph = withSafeRender(ClearGlyphImpl, "ClearGlyph");
